import { useEffect, useState } from 'react';
import { ShieldCheck, Download, FileText, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { irbApi } from '../lib/api';
import { toast } from 'sonner';

const formatSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

/**
 * Inline IRB resources panel — info text + downloadable docs.
 * Read-only; no submission. Used inside Study Creation Step 2 and Study Workspace.
 */
export function IRBPanel({ className = '' }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ info_text: '', documents: [], updated_at: null });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await irbApi.get();
        if (active) setData({
          info_text: res.data.info_text || '',
          documents: res.data.documents || [],
          updated_at: res.data.updated_at || null,
        });
      } catch {
        if (active) toast.error('Failed to load IRB resources');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleDownload = async (file) => {
    try {
      const url = irbApi.downloadUrl(file.id);
      const token = localStorage.getItem('token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      toast.error('Could not download file');
    }
  };

  return (
    <div
      className={`rounded-xl border border-yellow-200 bg-yellow-50/40 p-5 ${className}`}
      data-testid="irb-panel"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-yellow-700" />
          <h3 className="font-semibold text-slate-900 font-['Manrope']">IRB Resources</h3>
          <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">Available</Badge>
        </div>
        {data.updated_at && (
          <span className="text-xs text-slate-500" data-testid="irb-updated-at">
            Last updated {formatDate(data.updated_at)}
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-yellow-600 animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Info text */}
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">About IRB</p>
            {data.info_text ? (
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="irb-info-text">
                {data.info_text}
              </p>
            ) : (
              <p className="text-sm text-slate-400 italic">No IRB information has been published yet.</p>
            )}
          </div>

          {/* Documents */}
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Downloadable Documents</p>
            {data.documents.length === 0 ? (
              <p className="text-sm text-slate-400 italic">No documents have been published yet.</p>
            ) : (
              <div className="space-y-2">
                {data.documents.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:border-yellow-300 transition-colors"
                    data-testid={`irb-doc-${d.id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <FileText className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">{d.filename}</p>
                        <p className="text-xs text-slate-500">{formatSize(d.size)}</p>
                        {d.description && (
                          <p
                            className="text-xs text-slate-600 mt-1.5 leading-snug"
                            data-testid={`irb-doc-desc-${d.id}`}
                          >
                            {d.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(d)}
                      data-testid={`irb-download-${d.id}`}
                      className="flex-shrink-0"
                    >
                      <Download className="w-4 h-4 mr-1.5" /> Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default IRBPanel;
